import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, Input, OnDestroy, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PorIconDirective } from '@por/shared/ui/icon-lib';
import { Subscription, combineLatest, startWith, tap } from 'rxjs';
import { ApxError } from '../models/error.model';
import { ControlErrorMessageService } from '../services/control-error-message.service';
import { ApxControlDirective } from '../form/control.directive';
import { ApxFormGroupDirective } from '../form/form.directive';
import { ComponentSubscription } from '../utils/decorators/component-subscription';

let nextUniqueId = 0;

/**
 * Use this component as a host for specific form component instances. This component is responsible for
 * displaying common elements such as Hints, Errors and Label, as well as tieing into the reactive form
 * or allowing the component to be used standalone
 *
 * @example <caption>Usage with an <input> element using the ApxInputDirective</caption>
 * <apx-form-field label="username">
 *    <input apxInput type="text" formControlName="name" />
 * </apx-form-field>
 */
@Component({
    selector: 'apx-form-field',
    standalone: true,
    imports: [CommonModule, PorIconDirective],
    templateUrl: './form-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: []
})
export class ApxFormFieldComponent implements OnDestroy, AfterContentInit {
    private readonly formDirective = inject(ApxFormGroupDirective, { optional: true });
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly ctrlErrorMsgService = inject(ControlErrorMessageService);

    @ComponentSubscription sub!: Subscription;
    @ContentChild(ApxControlDirective) embeddedCtrl!: ApxControlDirective;
    @Input() size: 'standard' | 'small' = 'standard';
    @Input() set error(err: string | undefined) {
        if (err || err === '') {
            this._errorMessage = err;
        }
    }
    @Input() label!: string;
    @Input() hint: string | undefined;

    readonly icon = 'info';
    readonly inputId = `apx-input-${nextUniqueId++}`;
    readonly labelId = `apx-label-${nextUniqueId++}`;

    private reactiveControlInstance!: AbstractControl | null;
    private _errorMessage!: string;

    /**
     * errorMessage - Dependent on usage mode, ie. reactive or standalone,
     * will return either the message provided as an Input, or the error from the
     * reactive form control
     */
    get errorMessage(): string {
        let error: string;

        if (this.isReactive) {
            error = this._errorMessage ? this._errorMessage : this.reactiveControlInstance?.touched ? this.ctrlErrorMsgService.mapError(this.reactiveControlInstance?.errors ?? null) : '';
        } else {
            error = this._errorMessage;
        }

        this.setInvalidState(!!error);
        return error;
    }

    /**
     * If the component is being used in Reactive forms, there will be a FormControl instance
     */
    get isReactive(): boolean {
        return !!this.reactiveControlInstance;
    }

    /**
     * Connect the FormControl to this component
     */
    ngAfterContentInit(): void {
        if (this.formDirective?.formGroupDirective) {
            this.reactiveControlInstance = this.getEmbeddedControl();
            this.setSubmissionSub();
        }
    }

    /**
     * ngOnDestroy is necessary for the ComponentSubscription decorator
     * ignore the no-empty-lifecycle-method rule because the lifecycle method is
     * defined in the decorator
     */
    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
    ngOnDestroy(): void {}

    /**
     * Use the injected ApxControlDirective reference to get access to it's FormControl instance
     * @returns FormControl or null
     */
    private getEmbeddedControl() {
        if (!this.embeddedCtrl) {
            throw new ApxError('No ApxControlDirective found in projected content. ApxControlDirective must be added to an embedded form component element.');
        }
        return this.embeddedCtrl.getControl();
    }

    /**
     * Cascade the invalid state to the injected ApxControlDirective
     * reference when the form field has an error
     * @param isInvalid
     */
    private setInvalidState(isInvalid: boolean) {
        this.embeddedCtrl.isInvalid = isInvalid;
    }

    /**
     * Connect with the submit$ observable which is initialized in
     * the ApxFormDirective
     */
    private setSubmissionSub(): void {
        const streams = [(this.formDirective as ApxFormGroupDirective).submit$.pipe(startWith(false))];

        if (this.reactiveControlInstance) {
            streams.push(this.reactiveControlInstance?.statusChanges.pipe(startWith(this.reactiveControlInstance.value)));
        }

        this.sub = combineLatest(streams)
            .pipe(
                tap(() => {
                    /* Trigger change detection whenever the Form is submitted, or when the statusChanges emits
            from an embedded FormControl instance. This ensures we can display the error on form submission
            and when the status of the control changes between validity states */
                    this.detectChanges();
                })
            )
            .subscribe();
    }

    /**
     * Detect changes on both the ApxFormControl and ApxControlDirective instances
     * to ensure display of error messages in the Component and error styling in
     * the projected control
     */
    private detectChanges() {
        this.cdRef.detectChanges();
        this.embeddedCtrl.cdr.detectChanges();
    }
}
